/**
 * API Client for Monuite Call Intelligence
 * Source: 03_API_CONTRACT_OBSERVED.md
 */

import type {
  User,
  Call,
  Stats,
  Agent,
  SyncLog,
  ImportJob,
  ImportJobLog,
  PipelineStatus,
  LoginResponse,
  ApiError,
  CallFilters,
  PaginatedResponse,
} from '../types';

const API_BASE = '/api';

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Merge existing headers
    if (options.headers) {
      const existingHeaders = options.headers as Record<string, string>;
      Object.assign(headers, existingHeaders);
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiError;
      throw new Error(error.error?.message || `API Error: ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.token);
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  async getMe(): Promise<User> {
    return this.request<User>('/auth/me');
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; token: string }> {
    const response = await this.request<{ success: boolean; token: string }>(
      '/auth/change-password',
      {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }
    );
    this.setToken(response.token);
    return response;
  }

  // Calls endpoints
  async getCalls(
    filters: CallFilters = {},
    limit = 50,
    offset = 0
  ): Promise<PaginatedResponse<Call>> {
    const params = new URLSearchParams();

    if (filters.userId) params.set('userId', filters.userId);
    if (filters.phase) params.set('phase', filters.phase);
    if (filters.leadScore) params.set('leadScore', filters.leadScore);
    if (filters.status) params.set('status', filters.status);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.hasTranscript !== undefined)
      params.set('hasTranscript', String(filters.hasTranscript));
    if (filters.hasEvaluation !== undefined)
      params.set('hasEvaluation', String(filters.hasEvaluation));

    params.set('limit', String(limit));
    params.set('offset', String(offset));

    return this.request<PaginatedResponse<Call>>(`/calls?${params.toString()}`);
  }

  async getCall(id: string): Promise<Call> {
    return this.request<Call>(`/calls/${id}`);
  }

  async getStats(filters: CallFilters = {}): Promise<Stats> {
    const params = new URLSearchParams();

    if (filters.userId) params.set('userId', filters.userId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const query = params.toString();
    return this.request<Stats>(`/stats${query ? `?${query}` : ''}`);
  }

  async getAgents(): Promise<Agent[]> {
    const response = await this.request<{ agents: Agent[] }>('/agents');
    return response.agents;
  }

  // Pipeline endpoints
  async transcribeCall(callId: string): Promise<{ success: boolean }> {
    return this.request(`/pipeline/transcribe/${callId}`, { method: 'POST' });
  }

  async evaluateCall(callId: string): Promise<{ success: boolean }> {
    return this.request(`/pipeline/evaluate/${callId}`, { method: 'POST' });
  }

  async transcribeAll(): Promise<{ success: boolean; queued: number }> {
    return this.request('/pipeline/transcribe-all', { method: 'POST' });
  }

  async evaluateAll(): Promise<{ success: boolean; queued: number }> {
    return this.request('/pipeline/evaluate-all', { method: 'POST' });
  }

  // Sync endpoints
  async syncCall(callId: string): Promise<{ success: boolean }> {
    return this.request(`/sync/call/${callId}`, { method: 'POST' });
  }

  async syncAll(): Promise<{ success: boolean; queued: number }> {
    return this.request('/sync/all', { method: 'POST' });
  }

  async getSyncStatus(): Promise<PipelineStatus> {
    return this.request<PipelineStatus>('/sync/status');
  }

  async getRecentSyncLogs(limit = 20): Promise<SyncLog[]> {
    const response = await this.request<{ logs: SyncLog[] }>(
      `/sync/recent?limit=${limit}`
    );
    return response.logs;
  }

  async getCallSyncLogs(callId: string): Promise<SyncLog[]> {
    const response = await this.request<{ logs: SyncLog[] }>(
      `/sync/call/${callId}/logs`
    );
    return response.logs;
  }

  // Evaluation approval
  async approveEvaluation(callId: string): Promise<{ success: boolean }> {
    return this.request(`/calls/${callId}/approve`, { method: 'POST' });
  }

  async rejectEvaluation(callId: string): Promise<{ success: boolean }> {
    return this.request(`/calls/${callId}/reject`, { method: 'POST' });
  }

  // Admin - Import Jobs
  async getImportJobs(
    limit = 20,
    offset = 0
  ): Promise<{ jobs: ImportJob[] }> {
    return this.request<{ jobs: ImportJob[] }>(
      `/admin/import-jobs?limit=${limit}&offset=${offset}`
    );
  }

  async getImportJob(id: string): Promise<ImportJob> {
    return this.request<ImportJob>(`/admin/import-jobs/${id}`);
  }

  async getImportJobLogs(
    jobId: string,
    limit = 50,
    offset = 0
  ): Promise<{ logs: ImportJobLog[] }> {
    return this.request<{ logs: ImportJobLog[] }>(
      `/admin/import-jobs/${jobId}/logs?limit=${limit}&offset=${offset}`
    );
  }

  async createImportJob(data: {
    name: string;
    job_type: ImportJob['job_type'];
    start_date?: string;
    end_date?: string;
    batch_size?: number;
    delay_between_batches_ms?: number;
  }): Promise<ImportJob> {
    const response = await this.request<{ success: boolean; job: ImportJob }>(
      '/admin/import-jobs',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.job;
  }

  async startImportJob(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/import-jobs/${id}/start`, { method: 'POST' });
  }

  async pauseImportJob(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/import-jobs/${id}/pause`, { method: 'POST' });
  }

  async cancelImportJob(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/import-jobs/${id}/cancel`, { method: 'POST' });
  }

  async continueImportJob(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/import-jobs/${id}/continue`, { method: 'POST' });
  }

  async deleteImportJob(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/import-jobs/${id}`, { method: 'DELETE' });
  }

  // Admin - User management
  async getUsers(): Promise<User[]> {
    const response = await this.request<{ users: User[] }>('/auth/users');
    return response.users;
  }

  async createUser(data: {
    email: string;
    password: string;
    name: string;
    role: 'admin' | 'user';
  }): Promise<User> {
    const response = await this.request<{ success: boolean; user: User }>(
      '/auth/users',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.user;
  }

  async deleteUser(id: string): Promise<{ success: boolean }> {
    return this.request(`/auth/users/${id}`, { method: 'DELETE' });
  }
}

// Export singleton instance
export const api = new ApiClient();
export default api;
